import clsx from "clsx";
import { format, isSameDay } from "date-fns";

import type { CalendarDay } from "~/modules/dashboard/components/TimeTrackingCalendar/calendarDays.ts";
import { weekdayNames } from "~/modules/dashboard/components/TimeTrackingCalendar/calendarDays.ts";
import { getAbsenceColor } from "~/modules/dashboard/components/TimeTrackingCalendar/entryStyles.ts";
import type { DayStates } from "~/modules/timeTracking/api/timeTracking/timeTrackingTypes.ts";
import { isDayBooked } from "~/modules/timeTracking/utils/timeTrackingUtils.ts";

type Props = {
	days: CalendarDay[];
	dayStates: DayStates | undefined;
	selectedDate: Date;
	selectDate: (date: Date) => void;
};

/**
 * The month below `md`: each day is a number with markers, and a tap selects the day for the day list. Seven
 * columns at 375 px give about 48 px for each cell, which holds no title and no hours.
 * A booked day gets a green circle by the `DayState` of the backend (see `isDayBooked`), not by 8 hours. Today and the
 * selected day keep their own fill and show a booked state as a green border.
 */
export function CompactMonthGrid(props: Props) {
	const { days, dayStates, selectedDate, selectDate } = props;

	return (
		<div className="grid grid-cols-7 gap-y-1 text-sm">
			{weekdayNames.map((weekdayName) => (
				<div key={weekdayName}
					 className="py-1 text-center text-xs font-semibold text-gray-500">{weekdayName}</div>
			))}
			{days.map((day) => {
				const isSelected = day.isInMonth && isSameDay(day.date, selectedDate);
				const isBooked = day.isInMonth && isDayBooked(dayStates, day.date);
				return (
					<button key={day.date.toISOString()}
							type="button"
							disabled={!day.isInMonth}
							aria-pressed={isSelected}
							aria-label={format(day.date, "dd.MM.yyyy")}
							onClick={() => selectDate(day.date)}
							className="flex flex-col items-center gap-1 py-1 focus:outline-none">
						<span className={clsx("flex size-8 items-center justify-center rounded-full",
							// Today stays an orange circle when it is selected, so that today is always visible.
							day.isToday ? "bg-secondary-500 font-semibold text-white"
								: isSelected ? "bg-primary-500 font-semibold text-white"
									: isBooked ? "bg-success-500 font-semibold text-white"
										: day.isInMonth ? "text-gray-700" : "text-gray-300",
							(day.isToday || isSelected) && isBooked && "border-2 border-success-500")}>
							{format(day.date, "d")}
						</span>
						<span className={clsx("flex h-1.5 gap-0.5", !day.isInMonth && "opacity-40")}>
							{day.timeTrackings.length > 0 && <span className="size-1.5 rounded-full bg-primary-400" />}
							{day.absences.length > 0 && <span className={clsx("size-1.5 rounded-full", getAbsenceColor(day.absences[0].absenceName).solid)} />}
							{day.holiday && <span className="size-1.5 rounded-full bg-gray-400" />}
						</span>
					</button>
				);
			})}
		</div>
	);
}
